import React from "react";

const defaultContentSelector = "table";

/** Tracks whether a scroll container's content exceeds its visible height. */
export const useContainerOverflow = (
    enabled: boolean,
    contentDeps: React.DependencyList,
    contentSelector: string = defaultContentSelector,
) => {
    const [contentOverflows, setContentOverflows] = React.useState(false);

    // A callback ref (with its React 19 cleanup return) re-runs whenever the DOM node itself changes,
    // not just when contentDeps change — needed because some consumers (e.g. the mobile/desktop
    // navigation drawer) remount this container into a different tree location at runtime.
    const ref = React.useCallback(
        (container: HTMLDivElement | null) => {
            if (!container || !enabled) {
                setContentOverflows(false);
                return;
            }

            const updateOverflow = () => {
                setContentOverflows(container.scrollHeight > container.clientHeight + 1);
            };

            updateOverflow();

            const resizeObserver = new ResizeObserver(updateOverflow);
            resizeObserver.observe(container);

            const contentElement = container.querySelector(contentSelector);
            if (contentElement) {
                resizeObserver.observe(contentElement);
            }

            return () => resizeObserver.disconnect();
        },
        [enabled, contentSelector, ...contentDeps],
    );

    return { ref, contentOverflows };
};
